import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import plusIcon from "constants/modelCompareData/assets/plus.svg";
import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";
import { imageSrc } from "@ubnt/store.core/lib/utils/asset";
import * as S from "../../ProductList.styles";
import type { ProductProps } from "./Product.types";

export const Product = ({
  id,
  groupId,
  image,
  name,
  description,
  selectedProducts,
  setSelectedProducts,
  groupIsActive,
  groupIndex,
  groupLength,
  playAnimation,
  canPlayAniimation,
  selectedProductsLimit,
  setExternalState,
  showProductGroups,
  isStaticProduct,
  isOverviewPage,
  onClick,
}: ProductProps) => {
  const { formatMessage } = useModelCompareTranslation();
  const [startingClickPosition, setStartingClickPosition] = useState(0);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  const isSelected = useMemo(
    () => selectedProducts.some((product) => product.productId === id),
    [selectedProducts, id],
  );
  const isOnlySelectedProduct = useMemo(
    () => isSelected && selectedProducts.length === 1,
    [isSelected, selectedProducts],
  );
  const isDisabled = useMemo(
    () =>
      (!isSelected && selectedProducts.length === selectedProductsLimit) ||
      isOnlySelectedProduct,
    [
      isSelected,
      selectedProducts,
      selectedProductsLimit,
      isOnlySelectedProduct,
    ],
  );

  const buttonClickHandler = useCallback(() => {
    const newProductList = selectedProducts.filter(
      (product) => product.productId !== id,
    );
    if (setExternalState) {
      setExternalState((prev) =>
        isSelected
          ? newProductList
          : [...prev, { productId: id, groupId: groupId }],
      );
    }

    setSelectedProducts((prev) =>
      isSelected
        ? newProductList
        : [...prev, { productId: id, groupId: groupId }],
    );
  }, [
    selectedProducts,
    id,
    groupId,
    isSelected,
    setExternalState,
    setSelectedProducts,
  ]);

  const onPointerUpHandler = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.clientX === startingClickPosition) setHasBeenClicked(true);
  };

  useEffect(() => {
    if (hasBeenClicked) {
      onClick();
      setHasBeenClicked(false);
    }
  }, [hasBeenClicked, onClick]);

  return (
    <S.Product
      $isActive={groupIsActive}
      $groupIndex={groupIndex}
      $groupLength={groupLength}
      $playAnimation={playAnimation}
      $canPlayAniimation={canPlayAniimation}
      $productGroupsSelectorExists={showProductGroups}
    >
      <S.ProductCardOnClickArea
        onPointerDown={(event) => setStartingClickPosition(event.clientX)}
        onPointerUp={(event) => onPointerUpHandler(event)}
      />
      {image.src && (
        <S.Image
          loading="lazy"
          decoding="async"
          src={imageSrc(image.src)}
          alt={name}
          width={image.width}
          height={image.height}
        />
      )}
      <S.DescriptionButtonContainer>
        <S.Name>{name}</S.Name>
        {description && (
          <S.Description>{formatMessage(description)}</S.Description>
        )}

        <S.Button
          $isStaticProduct={!isOverviewPage && isStaticProduct}
          $isSelected={isSelected}
          $disabled={isDisabled}
          type="button"
          disabled={isDisabled}
          onClick={buttonClickHandler}
        >
          <S.ButtonIcon
            $isSelected={isSelected}
            src={plusIcon}
            width={20}
            height={20}
          />
          <S.ButtonCopy>
            {formatMessage(
              `commons.productList.button.${isSelected ? "active" : "inactive"}`,
            )}
            {isSelected && <S.CopyWhiteSpace />}
          </S.ButtonCopy>
        </S.Button>
      </S.DescriptionButtonContainer>
    </S.Product>
  );
};
